import type { JSX, ReactNode } from 'react';

interface InfoTabProps {
  description: string | null;
}

/** Callout-style styling for a parsed heading — matches the CMS's own "title callout" treatment
 *  for e.g. "**Hadestown + On Stage Photo Op with Cast Member ticket**". */
const HEADING_STYLE = {
  fontFamily: 'owners, sans-serif',
  fontWeight: 600,
  fontSize: '2rem',
};

/** Renders a line's inline `**bold**` runs as JSX nodes — plain text stays a plain string (React
 *  escapes it automatically), so nothing here ever builds an HTML string or needs
 *  `dangerouslySetInnerHTML`/sanitizing. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  return text
    .split(/\*\*(.+?)\*\*/g)
    .map((part, i) => (i % 2 === 1 ? <strong key={`${keyPrefix}-${i}`}>{part}</strong> : part));
}

/** Splits an accumulated paragraph's text on bare `**bold**` spans — each becomes its own `<h2>`
 *  block (the CMS content uses this bare form, with no leading `#` at all, for title-like lines
 *  such as an add-on ticket name); surrounding plain text renders as an ordinary paragraph. */
function pushParagraphBlocks(text: string, keyBase: string, blocks: ReactNode[]) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  parts.forEach((part, i) => {
    if (i % 2 === 1) {
      const trimmed = part.trim();
      if (!trimmed) return;
      blocks.push(
        <h2 key={`${keyBase}-${i}`} style={HEADING_STYLE}>
          <strong>{trimmed}</strong>
        </h2>,
      );
    } else if (part.trim()) {
      blocks.push(
        <p key={`${keyBase}-${i}`} className="shows-detail-info-text">
          {part}
        </p>,
      );
    }
  });
}

/**
 * Minimal, dependency-free stand-in for the CMS's own `marked.parse()` + `DOMPurify.sanitize()`
 * pipeline (its `theatre-detail-page.js` Markdown-parses the raw `data-description` text, then
 * sanitizes and injects the result as `innerHTML`). Rather than pull in a Markdown library plus a
 * sanitizer, this renders the two constructs the CMS content actually uses — ATX headings
 * (`#`...`######` at the start of a line) and `**bold**` — directly as JSX nodes. Nothing here
 * ever builds an HTML string, so there's no `dangerouslySetInnerHTML` and no sanitization step
 * needed: React escapes every text node on its own.
 *
 * Two heading paths, matching two real authoring patterns seen in CMS content:
 *   1. A genuine ATX heading line (`### Some Title`, optionally ALSO wrapped in `**` — e.g. CMS
 *      authors sometimes write "### **Some Title**" redundantly) → a real `<h{n}>` for that level,
 *      with `**bold**` still applied inside it (no leftover `#` and no double-marking).
 *   2. A `**Bold Title**` span with NO leading `#` at all → still promoted to its own `<h2>` block
 *      — the CMS content uses this bare form for title-like lines (e.g. an add-on ticket name).
 */
function renderDescription(description: string): ReactNode[] {
  const lines = description.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let paragraphLines: string[] = [];
  let blockIndex = 0;

  const flush = () => {
    if (paragraphLines.length === 0) return;
    const text = paragraphLines.join(' ').trim();
    paragraphLines = [];
    if (!text) return;
    blockIndex += 1;
    pushParagraphBlocks(text, `p${blockIndex}`, blocks);
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      flush();
      return;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      blockIndex += 1;
      const level = heading[1].length;
      const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;
      blocks.push(
        <HeadingTag key={`h${blockIndex}`} style={HEADING_STYLE}>
          {renderInline(heading[2].trim(), `h${blockIndex}`)}
        </HeadingTag>,
      );
      return;
    }
    paragraphLines.push(line);
  });
  flush();

  return blocks;
}

/** Info tab — brd-frontend.md §6.1, frd.md FR-TAB-01. The full, untruncated
 *  description (distinct from the Details Card's 250-char-truncated one).
 *  Hidden entirely by the parent `ShowDetailTabs` when there's no
 *  description at all — this component always renders its content when
 *  mounted. */
export function InfoTab({ description }: InfoTabProps) {
  return (
    <div className="shows-detail-tab-panel">
      <h4>Info</h4>
      {description ? renderDescription(description) : null}
    </div>
  );
}
